import { Close } from '@carbon/icons-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/shared/lib/cx';
import { Button } from './Button';
import s from './Feedback.module.css';
import { IconButton } from './IconButton';
import { StatusIcon, type Tone } from './status-icons';

export interface ToastOptions {
  tone?: Tone;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  /** Время показа, мс. По умолчанию 6 с; ошибки не исчезают сами (WCAG 2.2.1). */
  duration?: number | null;
}

interface ToastItem extends ToastOptions {
  id: number;
}

interface ToastApi {
  show: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Уведомления об итогах действий («Сохранено», «Не удалось отправить»). */
export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}

const MAX_VISIBLE = 3;

interface Announcement {
  id: number;
  text: string;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('ui');
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  /**
   * Озвучивание — через постоянные live-регионы (есть в DOM до появления текста), а не через role у самих
   * уведомлений: так экранные дикторы надёжно читают каждое новое уведомление ровно один раз.
   */
  const [announcements, setAnnouncements] = useState<{ polite: Announcement | null; assertive: Announcement | null }>({ polite: null, assertive: null });

  const dismiss = useCallback((id: number) => setItems((current) => current.filter((item) => item.id !== id)), []);
  const show = useCallback((options: ToastOptions) => {
    const id = nextId.current;
    nextId.current += 1;
    setItems((current) => [...current, { ...options, id }].slice(-MAX_VISIBLE));
    const announcement = { id, text: [options.title, options.description].filter(Boolean).join('. ') };
    setAnnouncements((current) => (options.tone === 'error' ? { ...current, assertive: announcement } : { ...current, polite: announcement }));
    return id;
  }, []);
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section className={s.toastRegion} aria-label={t('toast.region')}>
        {items.map((item) => (
          <ToastView key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
        ))}
      </section>
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {announcements.polite ? <span key={announcements.polite.id}>{announcements.polite.text}</span> : null}
      </div>
      <div className="visually-hidden" aria-live="assertive" aria-atomic="true">
        {announcements.assertive ? <span key={announcements.assertive.id}>{announcements.assertive.text}</span> : null}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const { t } = useTranslation('ui');
  const tone = item.tone ?? 'info';
  const duration = item.duration === undefined ? (tone === 'error' ? null : 6000) : item.duration;
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration ?? 0);

  useEffect(() => {
    if (duration === null || paused) return;
    const started = Date.now();
    const timer = window.setTimeout(onDismiss, remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current -= Date.now() - started;
    };
  }, [duration, paused, onDismiss]);

  return (
    <div
      className={cx(s.toast, s[`tone-${tone}`])}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <StatusIcon tone={tone} />
      <div className={s.content}>
        <p className={s.title}>{item.title}</p>
        {item.description ? <p className={s.text}>{item.description}</p> : null}
        {item.action ? (
          <div className={s.actions}>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => {
                item.action?.onClick();
                onDismiss();
              }}
            >
              {item.action.label}
            </Button>
          </div>
        ) : null}
      </div>
      <IconButton className={s.close} label={t('toast.dismiss')} icon={Close} size="sm" iconSize={16} onClick={onDismiss} tooltipPlacement="left" />
    </div>
  );
}
